import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Bot, CalendarClock, Mail, MessageSquare, NotebookPen, Phone, Send, Users, type LucideIcon } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import {
  ACTIVITY_TYPE_LABELS,
  CALL_OUTCOMES,
  CALL_OUTCOME_LABELS,
  LOGGABLE_ACTIVITY_TYPES,
  MESSAGE_STATUSES,
  NO_FOLLOW_UP_MESSAGE,
  TIMELINE_FILTERS,
  TIMELINE_FILTER_LABELS,
  type CallOutcome,
  type LoggableActivityType,
  type TimelineFilter,
  type TimelineItem,
} from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { Checkbox } from '../../../../components/Checkbox';
import { Chip } from '../../../../components/Chip';
import { DatePicker } from '../../../../components/DatePicker';
import { Field } from '../../../../components/Field';
import { Input, Textarea } from '../../../../components/Input';
import { ModuleGate } from '../../../../components/ModuleGate';
import { Popover, usePopover } from '../../../../components/Popover';
import { Segmented } from '../../../../components/Segmented';
import { SkeletonLines } from '../../../../components/Skeleton';
import { MESSAGE_STATUS_TONES, TASK_STATUS_TONES } from '../../../../components/status-tones';
import { FilteredEmptyState, ServerErrorState } from '../../../../components/system-states/SystemState';
import { errorMessage } from '../../../../lib/api-client';
import { formatDate, formatDateTime, formatDateTimeLong } from '../../../../lib/format';
import { useModule } from '../../../../lib/modules';
import { useToasts } from '../../../../stores/toasts';
import { TASKS_KEY, TIMELINE_KEY, communicationApi, useTimeline } from '../../../communication/api/communication-api';
import { ComposePanel } from '../../../communication/components/ComposePanel';
import styles from './CandidatePanel.module.css';

/** What the list's quick actions and the left side's buttons open the log on. */
export type PanelLogKind = 'email' | 'call' | 'note';

export function loggableOf(kind: PanelLogKind): LoggableActivityType {
  return kind === 'email' ? 'email_out' : kind;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const DHAKA_OFFSET_MS = 6 * 60 * 60 * 1000;
const COMPOSE_WIDTH = 460;
const MIN_NEXT_ACTION = 2;

/** Tomorrow in Dhaka, `YYYY-MM-DD` — a follow-up's sensible default (06-qol §7), as COMM-02. */
function tomorrowInDhaka(): string {
  return new Date(Date.now() + DHAKA_OFFSET_MS + DAY_MS).toISOString().slice(0, 10);
}

const ICONS: Readonly<Record<TimelineItem['type'], LucideIcon>> = {
  call: Phone,
  email: Mail,
  sms: MessageSquare,
  note: NotebookPen,
  meeting: Users,
  system: Bot,
};

const STATUS_LABELS: Readonly<Record<string, string>> = {
  queued: 'Sending',
  scheduled: 'Waiting to send',
  cancelled: 'Not sent',
  failed: 'Failed',
  bounced: 'Bounced',
};

interface ActivityTabProps {
  readonly candidateId: string;
  /** False for a viewer who may not log (a hiring manager): the timeline reads only. */
  readonly canLog: boolean;
  readonly kind: LoggableActivityType;
  readonly onKindChange: (kind: LoggableActivityType) => void;
}

/**
 * COMM-01 and COMM-02 in the pop-up's look (CandidatePanel.dc.html "Activity"): the log box on top
 * — kind, call outcome, what happened, Log, Message — then every call, email, note and system step.
 */
export function ActivityTab({ candidateId, canLog, kind, onKindChange }: ActivityTabProps): ReactNode {
  const [filter, setFilter] = useState<TimelineFilter>('all');
  const timeline = useTimeline({ kind: 'candidate', id: candidateId }, filter);
  const first = timeline.data?.pages[0];
  const items = timeline.data?.pages.flatMap((page) => page.items) ?? [];
  const can = first?.can ?? { log: false, message: false };

  return (
    <div className={styles.tabBody}>
      {canLog && can.log && <LogBox candidateId={candidateId} kind={kind} onKindChange={onKindChange} canMessage={can.message} />}
      <div className={styles.filterRow}>
        <Segmented label="Show" value={filter} options={TIMELINE_FILTERS.map((value) => ({ value, label: TIMELINE_FILTER_LABELS[value] }))} onChange={setFilter} />
      </div>
      <TimelineList timeline={timeline} items={items} filter={filter} canLog={canLog && can.log} onClearFilter={() => setFilter('all')} />
      {timeline.hasNextPage && (
        <div>
          <Button variant="outline" size="sm" isPending={timeline.isFetchingNextPage} onClick={() => void timeline.fetchNextPage()}>
            Show older
          </Button>
        </div>
      )}
    </div>
  );
}

function TimelineList({
  timeline,
  items,
  filter,
  canLog,
  onClearFilter,
}: {
  readonly timeline: ReturnType<typeof useTimeline>;
  readonly items: readonly TimelineItem[];
  readonly filter: TimelineFilter;
  readonly canLog: boolean;
  readonly onClearFilter: () => void;
}): ReactNode {
  if (timeline.error && !timeline.data) {
    // The server refuses this reader on purpose (§L23); a quiet note, never a generic error.
    if (timeline.error.status === 403) return <p className={styles.quiet}>Activity isn't available to you.</p>;
    return <ServerErrorState onRetry={() => void timeline.refetch()} />;
  }
  if (!timeline.data) return <SkeletonLines count={5} />;
  if (items.length === 0 && filter !== 'all') return <FilteredEmptyState filterCount={1} onClearFilters={onClearFilter} />;
  if (items.length === 0) {
    return (
      <div className={styles.empty}>
        <span className={styles.emptyTitle}>Nothing yet</span>
        <span className={styles.emptyBody}>{canLog ? 'Log a call or a note above, or send a message.' : 'Calls, emails and notes will collect here.'}</span>
      </div>
    );
  }
  return (
    <ol className={styles.timeline}>
      {items.map((item) => (
        <TimelineRow key={item.id} item={item} />
      ))}
    </ol>
  );
}

/**
 * COMM-02 — "type, outcome, note, next action + due date" (E13). With Tasks on it always asks
 * "No follow-up?" when no next action is set, as the quick-log bar does (master plan S26).
 */
function LogBox({ candidateId, kind, onKindChange, canMessage }: { readonly candidateId: string; readonly kind: LoggableActivityType; readonly onKindChange: (kind: LoggableActivityType) => void; readonly canMessage: boolean }): ReactNode {
  const queryClient = useQueryClient();
  const show = useToasts((state) => state.show);
  const isTasksOn = useModule('tasks');
  const { anchor: composeAnchor, triggerRef: composeRef, toggle: toggleCompose, close: closeCompose } = usePopover();
  const [outcome, setOutcome] = useState<CallOutcome>('no_answer');
  const [body, setBody] = useState('');
  const [nextAction, setNextAction] = useState('');
  const [dueOn, setDueOn] = useState(tomorrowInDhaka);
  const [noFollowUp, setNoFollowUp] = useState(false);
  const [asked, setAsked] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const target = { applicationId: null, candidateId };

  const log = async (): Promise<void> => {
    const hasNext = isTasksOn && nextAction.trim().length >= MIN_NEXT_ACTION;
    if (isTasksOn && !hasNext && !noFollowUp) {
      setAsked(true);
      return;
    }
    if (kind === 'note' && body.trim() === '') {
      show({ tone: 'warning', message: 'Write the note first' });
      return;
    }
    setIsSaving(true);
    try {
      await communicationApi.log({
        applicationId: null,
        candidateId,
        type: kind,
        outcome: kind === 'call' ? outcome : null,
        body: body.trim(),
        nextAction: hasNext ? { title: nextAction.trim(), dueOn } : null,
        noFollowUp: !hasNext,
      });
      await Promise.all([queryClient.invalidateQueries({ queryKey: TIMELINE_KEY }), queryClient.invalidateQueries({ queryKey: TASKS_KEY })]);
      setBody('');
      setNextAction('');
      setNoFollowUp(false);
      setAsked(false);
      show({ tone: 'success', message: `${ACTIVITY_TYPE_LABELS[kind]} logged`, detail: hasNext ? 'The follow-up is on your task list.' : undefined });
    } catch (cause) {
      show({ tone: 'danger', message: "It wasn't logged", detail: errorMessage(cause) });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form
      className={styles.logBox}
      aria-label="Log an activity"
      onSubmit={(event) => {
        event.preventDefault();
        void log();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
          event.preventDefault();
          void log();
        }
      }}
    >
      <div className={styles.logRow}>
        <Segmented label="What happened" value={kind} options={LOGGABLE_ACTIVITY_TYPES.map((value) => ({ value, label: ACTIVITY_TYPE_LABELS[value] }))} onChange={onKindChange} />
        <span className={styles.spacer} />
        {canMessage && (
          <Button ref={composeRef} variant="outline" size="sm" icon={Send} onClick={toggleCompose} aria-haspopup="dialog">
            Message
          </Button>
        )}
      </div>
      {kind === 'call' && (
        <div className={styles.logRow}>
          <span className={styles.logLabel}>Outcome</span>
          <Segmented label="Outcome" value={outcome} options={CALL_OUTCOMES.map((value) => ({ value, label: CALL_OUTCOME_LABELS[value] }))} onChange={setOutcome} />
        </div>
      )}
      <Textarea aria-label="What happened" rows={2} value={body} maxLength={4000} placeholder={kind === 'note' ? 'Write the note' : 'What happened?'} onChange={(event) => setBody(event.target.value)} />
      <ModuleGate module="tasks">
        <div className={styles.followRow}>
          <Field label="Next action" isOptional>
            {({ inputId }) => (
              <Input
                id={inputId}
                value={nextAction}
                maxLength={200}
                placeholder="Call back about the interview time"
                onChange={(event) => {
                  setNextAction(event.target.value);
                  setAsked(false);
                }}
              />
            )}
          </Field>
          <Field label="Due">{({ inputId }) => <DatePicker id={inputId} label="Due" value={dueOn} onChange={setDueOn} timeZone="Asia/Dhaka" />}</Field>
        </div>
      </ModuleGate>
      <div className={styles.logFoot}>
        <ModuleGate module="tasks">
          <Checkbox
            checked={noFollowUp}
            onChange={(checked) => {
              setNoFollowUp(checked);
              setAsked(false);
            }}
            label="No follow-up needed"
          />
        </ModuleGate>
        {asked && (
          <p className={styles.ask} role="alert">
            {NO_FOLLOW_UP_MESSAGE}
          </p>
        )}
        <span className={styles.spacer} />
        <Button variant="solid" size="sm" type="submit" isPending={isSaving}>
          Log
        </Button>
      </div>
      <Popover anchor={composeAnchor} onClose={closeCompose} align="right" width={COMPOSE_WIDTH} label="Message">
        <ComposePanel target={target} onCancel={closeCompose} onDone={closeCompose} />
      </Popover>
    </form>
  );
}

function TimelineRow({ item }: { readonly item: TimelineItem }): ReactNode {
  const Icon = ICONS[item.type];
  const messageStatus = MESSAGE_STATUSES.find((status) => status === item.messageStatus);
  const messageLabel = messageStatus ? STATUS_LABELS[messageStatus] : undefined;
  return (
    <li className={styles.timelineItem}>
      <span className={styles.timelineIcon} data-system={item.isSystem || undefined} aria-hidden>
        <Icon size={14} />
      </span>
      <div className={styles.timelineBody}>
        <span className={styles.timelineTitle}>{item.title}</span>
        <span className={styles.timelineWhen} title={formatDateTimeLong(item.at)}>
          {[item.isSystem ? 'Automated' : item.actorName, formatDateTime(item.at)].filter(Boolean).join(' · ')}
          {item.application && (
            <>
              {' · '}
              <Link to="/applications/$applicationId" params={{ applicationId: item.application.id }}>
                {item.application.jobTitle}
              </Link>
            </>
          )}
        </span>
        {item.body && <p className={styles.timelineNote}>{item.body}</p>}
        {(messageStatus && messageLabel) || item.task ? (
          <span className={styles.chips}>
            {messageStatus && messageLabel && (
              <Chip tone={MESSAGE_STATUS_TONES[messageStatus]} plain>
                {messageLabel}
              </Chip>
            )}
            {item.task && (
              <Chip tone={TASK_STATUS_TONES[item.task.status]} plain>
                <CalendarClock size={12} aria-hidden /> {item.task.status === 'done' ? 'Follow-up done' : `Follow-up ${item.task.dueAt ? formatDate(item.task.dueAt) : ''}`}
              </Chip>
            )}
          </span>
        ) : null}
      </div>
    </li>
  );
}
